import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HelpAnchor,
  SkeletonBlock,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { Disclosure } from '@/versions/v1/shared/Disclosure';
import { useDrawer } from '@/app/providers';
import { unpackTable } from '@/versions/v1/shared/columnar';
import { useOemData } from '@/versions/v1/shared/useOemData';
import type { WorkOrder } from '@/versions/v1/schema/work-order';
import type { MineCode } from '@/versions/v1/schema/machine';
import { MeasureTile, SceneSection, SourceLink, TonePill } from '@/versions/v1/shared/oem-tiles';
import { BarChart } from '@/versions/v1/shared/viz/BarChart';
import { StackedBar, StackedBarLegend } from '@/versions/v1/shared/viz/StackedBar';
import { RingStat as RingGauge } from '@/versions/v1/shared/viz/RingStat';
import { COST_CATEGORIES, COST_CATEGORY_COLOR, seriesColor } from '@/versions/v1/shared/viz/tokens';
import {
  categoryBars,
  categorySource,
  classCostMatrix,
  className_,
  costByCrewRows,
  costByMineRows,
  costByMonthRows,
  costHeadline,
  costToServeSource,
  DIESEL_FACTOR,
  energyBySite,
  energySource,
  fmt$,
  fmtk,
  groupNum,
  intensityByClassAndMine,
  intensityByDrive,
  ledgerSource,
  perMachineSource,
  rateCard,
  rateCardSource,
  siteCostTiles,
  topMachineRows,
  type SourceTable,
} from './CostSummaryScene.transform';

// =========================================================================
// Cost summary — the demo's costs block, source line 2380. Fork-owned.
// Route `/costs`.
//
// This is the page that carries the first half of the value story: every
// dollar of running a mixed fleet, on one basis, across systems that do not
// talk to each other. The argument only lands if the reader can get from any
// figure to the rows behind it in one click, so every headline here opens the
// source drawer — the ledger, the per-machine roll-up, the rate card, the
// energy log.
//
// The rate card gets its own section rather than a footnote because it is
// what makes the largest ledger category defensible: lost production is
// *priced* from the customer's contract, not estimated, and a sceptical
// reader goes straight there.
// =========================================================================

/** Domains this page needs: the ledger, the work orders behind the shares. */
function useCostData() {
  const costs = useOemData('costs');
  const wos = useOemData('work-orders');
  const telemetry = useOemData('telemetry');

  const loading = costs.loading || wos.loading || telemetry.loading;
  const error = costs.error ?? wos.error ?? telemetry.error ?? null;

  const data = useMemo(() => {
    if (!costs.data || !wos.data || !telemetry.data) return null;
    return {
      byMonthMine: costs.data.cost_by_month_mine,
      byCrewCat: costs.data.cost_by_crew_cat,
      costByMachineCat: costs.data.cost_by_machine_cat,
      workOrders: unpackTable<WorkOrder>(wos.data.wos),
      energy: telemetry.data.energy,
    };
  }, [costs.data, wos.data, telemetry.data]);

  return { data, loading, error };
}

export function CostSummaryScene() {
  const navigate = useNavigate();
  const { open: openDrawer } = useDrawer();
  const { data, loading, error } = useCostData();

  const showSource = useCallback(
    (table: SourceTable, kicker = 'Source') => {
      openDrawer('oem-source', {
        title: table.title,
        kicker,
        wide: true,
        data: table as unknown as Record<string, unknown>,
      });
    },
    [openDrawer],
  );

  const view = useMemo(() => {
    if (!data) return null;
    const ctx = { workOrders: data.workOrders };
    const months = costByMonthRows(data.byMonthMine);
    return {
      headline: costHeadline(ctx, data.byMonthMine),
      siteTiles: siteCostTiles(data.byMonthMine),
      byMine: costByMineRows(data.byMonthMine),
      byCrew: costByCrewRows(data.byCrewCat),
      byMonth: months,
      categories: categoryBars({ costByMachineCat: data.costByMachineCat }),
      classes: classCostMatrix(),
      topMachines: topMachineRows(),
      rates: rateCard(),
      energy: energyBySite(data.energy),
      drives: intensityByDrive(data.energy),
      classMine: intensityByClassAndMine(data.energy),
      ctx,
    };
  }, [data]);

  const openMineLedger = useCallback(
    (mine: MineCode) => {
      if (!data) return;
      showSource(ledgerSource(data.byMonthMine, mine), 'Ledger');
    },
    [data, showSource],
  );

  return (
    <SurfacePageLayout width="full">
      <Commentable name="CostSummaryScene">
        <SurfacePageBody className="@container max-w-[1240px] space-y-9 pt-6">
          {/*
            C2. The breadcrumb still says "Cost summary" — that is the page's
            name in the product. The H1 says what the page is for, in the
            words the buyer uses about their own problem: the dollars exist,
            they just live in systems that were never introduced to each
            other. A report title cannot carry that.

            No kicker: B2 removed the one here because the breadcrumb already
            says "Cost summary", and that is still true now that the H1 has
            changed. The breadcrumb keeps the product name; the H1 makes the
            argument.
          */}
          <SurfacePageHeader
            title="Every dollar, across systems that never talk to each other"
            description="Every dollar in the ledger traces to a work order, a machine, a crew and a supplier."
            actions={
              <div className="flex items-center gap-3">
                <TonePill tone="flat">18 months · SAP PM + ledger · 12 days energy</TonePill>
                <HelpAnchor doc="costs/summary" />
              </div>
            }
          />


          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              The ledger could not be loaded ({error.message}). No figure is shown rather
              than a figure that might be partial.
            </div>
          ) : null}

          {/* ------------------------------------------------------- */}
          {/* Headline                                                */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Headline">
            {view && data ? (
              <div className="grid grid-cols-1 gap-4 @5xl:grid-cols-[minmax(0,1fr)_auto]">
                <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-3">
                  <MeasureTile
                    eyebrow="Total ledger · 18 months"
                    value={fmtk(view.headline.ledger)}
                    unit="across six sites"
                    detail={`${fmtk(view.headline.lostProduction)} of it is lost production — priced from the rate card, not estimated`}
                    tone="warn"
                    onSelect={() => showSource(ledgerSource(data.byMonthMine), 'Ledger')}
                    selectHint="Opens the monthly ledger rows."
                  />
                  <MeasureTile
                    eyebrow="Cost per operating hour"
                    value={fmt$(view.headline.meanPerOpHour)}
                    unit="per machine, all-in"
                    detail={`Highest site runs at ${fmt$(view.headline.highestSitePerOpHour)} an hour`}
                    tone="flat"
                    onSelect={() => showSource(perMachineSource(), 'Cost per machine')}
                    selectHint="Opens the per-machine cost roll-up."
                  />
                  <MeasureTile
                    eyebrow="Corrective share"
                    value={`${view.headline.correctivePct}%`}
                    unit="of all work orders"
                    detail="The share the operating model is built to move from unplanned to planned"
                    tone={view.headline.correctivePct > 50 ? 'bad' : 'warn'}
                    onSelect={() => showSource(costToServeSource(view.ctx), 'Cost to serve')}
                    selectHint="Opens the cost-to-serve table."
                  />
                </div>
                <div
                  className="flex items-center gap-4 rounded-xl border px-5 py-4"
                  style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                >
                  <RingGauge
                    pct={view.headline.lostPct}
                    sub="lost prod"
                    tone={view.headline.lostPct >= 40 ? 'bad' : 'warn'}
                    title="Lost production as a share of the 18-month ledger"
                    size={96}
                  />
                  <p
                    className="max-w-[15rem] text-[11.5px] leading-[1.55]"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    Lost production is the largest single category and the one the
                    customer feels. It is priced per machine class per mine from the
                    contract, which is what makes the rest of this page comparable
                    between sites.
                  </p>
                </div>
              </div>
            ) : (
              <SkeletonCardGrid count={4} cols={2} />
            )}
          </Commentable>

          {/* B3: the ask chips sit under the first numbers, not above them. */}
          <AskStrip view="costs" />

          {/* ------------------------------------------------------- */}
          {/* Per-site tiles                                          */}
          {/* ------------------------------------------------------- */}
          <Commentable name="SiteTiles">
            <SceneSection
              title="By site"
              subtitle="18-month ledger, lost production share, and how the site is run"
            >
              {view ? (
                <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3">
                  {view.siteTiles.map((tile) => (
                    <MeasureTile
                      key={tile.mine}
                      eyebrow={`${tile.name} · ${tile.machines} machines`}
                      value={fmtk(tile.total)}
                      unit={`${tile.lostPct}% lost production`}
                      detail={`${fmt$(tile.labourRate)}/h labour · ${tile.operatorModel} · ${tile.dataMode}`}
                      tone={tile.lostPct >= 45 ? 'bad' : tile.lostPct >= 35 ? 'warn' : 'good'}
                      onSelect={() => openMineLedger(tile.mine)}
                      selectHint="Opens this site's monthly ledger rows."
                    />
                  ))}
                </div>
              ) : (
                <SkeletonCardGrid count={6} cols={3} />
              )}
            </SceneSection>
          </Commentable>

          {/*
            B6. The executive argument is complete after `By site`: the total,
            the cost per operating hour, the corrective share, the lost-
            production ring and where it sits by mine. What follows is nine
            sections of analyst material — it is the right material and it
            stays, but it stays behind a band that says how much of it there
            is rather than in front of an executive who asked one question.
          */}
          <Disclosure
            label="More cost views"
            count="9 sections"
            hint="Cost by site and category, where the cost sits, cost per operating hour by class, by crew, the most expensive machines, the monthly ledger, cost to serve, the rate card, and energy and emissions."
          >
            {/* ------------------------------------------------------- */}
            {/* Cost by mine + category totals                          */}
            {/* ------------------------------------------------------- */}
            <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-2">
              <Commentable name="ByMine">
                <SceneSection
                  title="Cost by site and category"
                  subtitle="A$ over 18 months"
                  actions={
                    data ? (
                      <SourceLink
                        onClick={() => showSource(ledgerSource(data.byMonthMine), 'Ledger')}
                      >
                        Ledger rows
                      </SourceLink>
                    ) : null
                  }
                >
                  {view ? (
                    <div
                      className="rounded-xl border p-4"
                      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                    >
                      <StackedBar
                        title="Cost by site and category over 18 months"
                        rows={view.byMine}
                        categories={COST_CATEGORIES}
                        colors={COST_CATEGORY_COLOR}
                        format={fmtk}
                        axisFormat={axisMoney}
                        gutter={118}
                      />
                      <div className="mt-3">
                        <StackedBarLegend
                          categories={COST_CATEGORIES}
                          colors={COST_CATEGORY_COLOR}
                        />
                      </div>
                    </div>
                  ) : (
                    <SkeletonBlock className="h-[280px] rounded-xl" />
                  )}
                </SceneSection>
              </Commentable>

              <Commentable name="Categories">
                <SceneSection
                  title="Where the cost sits"
                  subtitle="whole base, largest category first"
                  actions={
                    data ? (
                      <SourceLink
                        onClick={() =>
                          showSource(
                            categorySource({ costByMachineCat: data.costByMachineCat }),
                            'Categories',
                          )
                        }
                      >
                        Category rows
                      </SourceLink>
                    ) : null
                  }
                >
                  {view ? (
                    <div
                      className="rounded-xl border p-4"
                      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                    >
                      <BarChart
                        title="Total cost by ledger category"
                        rows={view.categories.map((row) => ({
                          ...row,
                          color: COST_CATEGORY_COLOR[
                            row.label as keyof typeof COST_CATEGORY_COLOR
                          ],
                        }))}
                        format={fmtk}
                        gutter={118}
                        rowHeight={34}
                        showTrack
                      />
                    </div>
                  ) : (
                    <SkeletonBlock className="h-[280px] rounded-xl" />
                  )}
                </SceneSection>
              </Commentable>
            </div>

            {/* ------------------------------------------------------- */}
            {/* Cost per operating hour by class                        */}
            {/* ------------------------------------------------------- */}
            <Commentable name="ClassMatrix">
              <SceneSection
                title="Cost per operating hour by machine class"
                subtitle="A$/h all-in, mean of the machines in class"
                actions={
                  <SourceLink onClick={() => showSource(perMachineSource(), 'Cost per machine')}>
                    Per-machine rows
                  </SourceLink>
                }
              >
                {view ? (
                  <div
                    className="overflow-hidden rounded-xl border"
                    style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[42rem] border-collapse text-left">
                        <caption className="sr-only">
                          Mean cost per operating hour by site and machine class. The
                          highest site in each class is marked.
                        </caption>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                            <th
                              scope="col"
                              className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
                              style={{ color: 'var(--ink-tertiary)' }}
                            >
                              Site
                            </th>
                            {view.classes.classes.map((cls) => (
                              <th
                                key={cls}
                                scope="col"
                                className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.08em]"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                {className_(cls)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {view.classes.rows.map((row) => (
                            <tr key={row.mine} style={{ borderTop: '1px solid var(--hairline)' }}>
                              <th
                                scope="row"
                                className="px-4 py-2.5 text-left text-[12px] font-medium"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {row.name}
                              </th>
                              {view.classes.classes.map((cls) => {
                                const value = row.byClass[cls];
                                const worst = view.classes.worstByClass[cls] === row.mine;
                                return (
                                  <td
                                    key={cls}
                                    className="mono px-4 py-2.5 text-right text-[12px]"
                                    style={{
                                      color: worst ? 'var(--crit)' : 'var(--ink-secondary)',
                                      fontWeight: worst ? 600 : 400,
                                    }}
                                  >
                                    {value == null ? '—' : fmt$(Math.round(value))}
                                    {worst ? <span className="sr-only"> (highest)</span> : null}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p
                      className="px-4 py-3 text-[11.5px] leading-[1.6]"
                      style={{
                        color: 'var(--ink-tertiary)',
                        borderTop: '1px solid var(--hairline)',
                      }}
                    >
                      Kalbarri is highest in every underground class despite the shallowest
                      ground and the cheapest labour: reactive work averages 9 h down
                      against 5 h elsewhere and a third of PMs run late. Mount Isolde's
                      absolute spend is higher because an hour of a truck at 1,650 m is
                      worth A$2,200 — but its per-hour cost is lower. Gawler Deep's loaders
                      carry the L-18 fire; Ochre Hill's drills carry the parted string.
                    </p>
                  </div>
                ) : (
                  <SkeletonBlock className="h-64 rounded-xl" />
                )}
              </SceneSection>
            </Commentable>

            {/* ------------------------------------------------------- */}
            {/* Crew + top machines                                     */}
            {/* ------------------------------------------------------- */}
            <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-2">
              <Commentable name="ByCrew">
                <SceneSection
                  title="Cost by crew"
                  subtitle="A$ over 18 months, direct plus lost production"
                >
                  {view ? (
                    <div
                      className="rounded-xl border p-4"
                      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                    >
                      <StackedBar
                        title="Cost by crew over 18 months"
                        rows={view.byCrew.slice(0, 12)}
                        categories={COST_CATEGORIES}
                        colors={COST_CATEGORY_COLOR}
                        format={fmtk}
                        axisFormat={axisMoney}
                        gutter={156}
                        rowHeight={26}
                      />
                      <p
                        className="mt-3 text-[11px]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        Twelve highest of {view.byCrew.length} crews.
                      </p>
                    </div>
                  ) : (
                    <SkeletonBlock className="h-[360px] rounded-xl" />
                  )}
                </SceneSection>
              </Commentable>

              <Commentable name="TopMachines">
                <SceneSection
                  title="Most expensive machines per operating hour"
                  subtitle="A$/h all-in"
                >
                  {view ? (
                    <div
                      className="overflow-hidden rounded-xl border"
                      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                    >
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[32rem] border-collapse text-left">
                          <caption className="sr-only">
                            The ten machines with the highest all-in cost per operating
                            hour. Each machine opens its own page.
                          </caption>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                              {['Machine', 'Site', 'Model'].map((h) => (
                                <th
                                  key={h}
                                  scope="col"
                                  className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                >
                                  {h}
                                </th>
                              ))}
                              {['A$ / op h', '18 mo all-in'].map((h) => (
                                <th
                                  key={h}
                                  scope="col"
                                  className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.08em]"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {view.topMachines.map((m) => (
                              <tr
                                key={m.machineId}
                                style={{ borderTop: '1px solid var(--hairline)' }}
                              >
                                <td className="px-4 py-2.5">
                                  <SourceLink
                                    onClick={() => navigate(`/machines/${m.machineId}`)}
                                    className="mono !text-[12px]"
                                  >
                                    {m.machineId}
                                  </SourceLink>
                                </td>
                                <td
                                  className="px-4 py-2.5 text-[12px]"
                                  style={{ color: 'var(--ink-secondary)' }}
                                >
                                  {m.site}
                                </td>
                                <td
                                  className="mono px-4 py-2.5 text-[12px]"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                >
                                  {m.model}
                                </td>
                                <td
                                  className="mono px-4 py-2.5 text-right text-[12px]"
                                  style={{ color: 'var(--ink-primary)' }}
                                >
                                  {fmt$(m.perOpHour)}
                                </td>
                                <td
                                  className="mono px-4 py-2.5 text-right text-[12px]"
                                  style={{ color: 'var(--ink-secondary)' }}
                                >
                                  {groupNum(m.total)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <SkeletonBlock className="h-[360px] rounded-xl" />
                  )}
                </SceneSection>
              </Commentable>
            </div>

            {/* ------------------------------------------------------- */}
            {/* Monthly ledger                                          */}
            {/* ------------------------------------------------------- */}
            <Commentable name="MonthlyLedger">
              <SceneSection
                title="Monthly ledger by site"
                subtitle="A$ per month by work-order creation date, to Aug 2026"
                actions={
                  data ? (
                    <SourceLink
                      onClick={() => showSource(ledgerSource(data.byMonthMine), 'Ledger')}
                    >
                      Ledger rows
                    </SourceLink>
                  ) : null
                }
              >
                {view ? (
                  <div
                    className="rounded-xl border p-4"
                    style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                  >
                    <StackedBar
                      title="Monthly ledger by site"
                      rows={view.byMonth.rows}
                      categories={view.byMonth.categories}
                      format={fmtk}
                      axisFormat={axisMoney}
                      gutter={96}
                      rowHeight={26}
                      viewWidth={1120}
                    />
                    <div className="mt-3">
                      <StackedBarLegend
                        categories={view.byMonth.categories}
                        colors={Object.fromEntries(
                          view.byMonth.categories.map((c, i) => [c, seriesColor(i)]),
                        )}
                      />
                    </div>
                  </div>
                ) : (
                  <SkeletonBlock className="h-[420px] rounded-xl" />
                )}
              </SceneSection>
            </Commentable>

            {/* ------------------------------------------------------- */}
            {/* Cost to serve                                           */}
            {/* ------------------------------------------------------- */}
            <Commentable name="CostToServe">
              <SceneSection
                title="Cost to serve by site"
                subtitle="per operating hour, against the labour rate and the corrective share"
                actions={
                  view ? (
                    <SourceLink
                      onClick={() => showSource(costToServeSource(view.ctx), 'Cost to serve')}
                    >
                      Source rows
                    </SourceLink>
                  ) : null
                }
              >
                {view ? (
                  <div
                    className="overflow-hidden rounded-xl border"
                    style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[42rem] border-collapse text-left">
                        <caption className="sr-only">
                          Cost to serve by site: machines, 18-month ledger, cost per
                          operating hour, labour rate and corrective share.
                        </caption>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                            <th
                              scope="col"
                              className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
                              style={{ color: 'var(--ink-tertiary)' }}
                            >
                              Site
                            </th>
                            {['Machines', 'Ledger', 'A$ / op h', 'Labour A$/h', 'Corrective'].map(
                              (h) => (
                                <th
                                  key={h}
                                  scope="col"
                                  className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.08em]"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                >
                                  {h}
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {view.headline.sites.map((s) => (
                            <tr key={s.mine} style={{ borderTop: '1px solid var(--hairline)' }}>
                              <th
                                scope="row"
                                className="px-4 py-2.5 text-left text-[12px] font-medium"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                <SourceLink
                                  onClick={() => navigate(`/sites/${s.mine}`)}
                                  className="!text-[12px] !font-medium"
                                >
                                  {MINE_NAME(s.mine, view.siteTiles)}
                                </SourceLink>
                              </th>
                              <td
                                className="mono px-4 py-2.5 text-right text-[12px]"
                                style={{ color: 'var(--ink-secondary)' }}
                              >
                                {s.machines}
                              </td>
                              <td
                                className="mono px-4 py-2.5 text-right text-[12px]"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {fmtk(s.total)}
                              </td>
                              <td
                                className="mono px-4 py-2.5 text-right text-[12px]"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {fmt$(s.costPerOpHour)}
                              </td>
                              <td
                                className="mono px-4 py-2.5 text-right text-[12px]"
                                style={{ color: 'var(--ink-secondary)' }}
                              >
                                {fmt$(s.labourRate)}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <TonePill
                                  tone={
                                    s.correctivePct >= 60
                                      ? 'bad'
                                      : s.correctivePct >= 45
                                        ? 'warn'
                                        : 'good'
                                  }
                                >
                                  {s.correctivePct}%
                                </TonePill>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <SkeletonBlock className="h-64 rounded-xl" />
                )}
              </SceneSection>
            </Commentable>

            {/* ------------------------------------------------------- */}
            {/* Rate card                                               */}
            {/* ------------------------------------------------------- */}
            <Commentable name="RateCard">
              <SceneSection
                title="Rate card"
                subtitle="what an hour of labour and an hour of lost production cost at each site"
                actions={
                  <SourceLink onClick={() => showSource(rateCardSource(), 'Rate card')}>
                    Source rows
                  </SourceLink>
                }
              >
                {view ? (
                  <div
                    className="overflow-hidden rounded-xl border"
                    style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[52rem] border-collapse text-left">
                        <caption className="sr-only">
                          Contracted labour rate, distribution centre, freight days and
                          priced lost production per machine class, by site.
                        </caption>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                            <th
                              scope="col"
                              className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
                              style={{ color: 'var(--ink-tertiary)' }}
                            >
                              Site
                            </th>
                            <th
                              scope="col"
                              className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.08em]"
                              style={{ color: 'var(--ink-tertiary)' }}
                            >
                              Labour
                            </th>
                            <th
                              scope="col"
                              className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
                              style={{ color: 'var(--ink-tertiary)' }}
                            >
                              Distribution centre
                            </th>
                            {view.rates.classes.map((cls) => (
                              <th
                                key={cls}
                                scope="col"
                                className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.08em]"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                {className_(cls)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {view.rates.rows.map((row) => (
                            <tr key={row.mine} style={{ borderTop: '1px solid var(--hairline)' }}>
                              <th
                                scope="row"
                                className="px-4 py-2.5 text-left text-[12px] font-medium"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {row.name}
                              </th>
                              <td
                                className="mono px-4 py-2.5 text-right text-[12px]"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {fmt$(row.labour)}
                              </td>
                              <td
                                className="px-4 py-2.5 text-[12px]"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                {row.dc} · {row.freightDays} d
                              </td>
                              {view.rates.classes.map((cls) => {
                                const value = row.lost[cls];
                                return (
                                  <td
                                    key={cls}
                                    className="mono px-4 py-2.5 text-right text-[12px]"
                                    style={{ color: 'var(--ink-secondary)' }}
                                  >
                                    {value == null ? '—' : fmt$(value)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p
                      className="px-4 py-3 text-[11.5px]"
                      style={{
                        color: 'var(--ink-tertiary)',
                        borderTop: '1px solid var(--hairline)',
                      }}
                    >
                      Class columns are lost production, A$ per hour the machine is down —
                      the customer's contracted figure, not an estimate.
                    </p>
                  </div>
                ) : (
                  <SkeletonBlock className="h-64 rounded-xl" />
                )}
              </SceneSection>
            </Commentable>

            {/* ------------------------------------------------------- */}
            {/* Energy and emissions                                    */}
            {/* ------------------------------------------------------- */}
            <Commentable name="Energy">
              <SceneSection
                title="Energy and emissions"
                subtitle="last 12 days"
                actions={
                  data ? (
                    <SourceLink onClick={() => showSource(energySource(data.energy), 'Energy')}>
                      Per-machine rows
                    </SourceLink>
                  ) : null
                }
              >
                {view ? (
                  <>
                    <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @5xl:grid-cols-4">
                      {view.energy.map((e) => (
                        <MeasureTile
                          key={e.mine}
                          eyebrow={e.name}
                          value={e.co2t.toFixed(1)}
                          unit="t CO₂-e"
                          detail={`${groupNum(Math.round(e.diesel))} L diesel · ${groupNum(Math.round(e.kwh))} kWh · ${e.perHour.toFixed(1)} kg/op h · ${e.powerSource}`}
                          tone="flat"
                        />
                      ))}
                      <MeasureTile
                        eyebrow="Emission factors"
                        value={DIESEL_FACTOR}
                        unit="kg CO₂-e per litre of diesel"
                        detail={`Grid: ${view.energy
                          .map((e) => `${e.name.split(' ')[0]} ${e.gridFactor}`)
                          .join(' · ')} kg/kWh`}
                        tone="flat"
                      />
                    </div>

                    {/*
                      Demo line 2399 `.costgrid`: the two intensity charts side
                      by side — by drive type (`ch-en1`, line 2403) and by
                      machine class and mine (`ch-en2`, line 2404). Both are
                      the mean of the machines' kg CO₂-e per operating hour.
                    */}
                    <div className="mt-3 grid grid-cols-1 items-start gap-3 @4xl:grid-cols-2">
                      <div
                        className="min-w-0 rounded-xl border p-4"
                        style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                      >
                        <h3
                          className="mb-3 text-[12.5px] font-semibold"
                          style={{ color: 'var(--ink-primary)' }}
                        >
                          Emissions intensity by drive type
                        </h3>
                        <BarChart
                          title="Mean kilograms of CO2 equivalent per operating hour, by drive type"
                          rows={view.drives}
                          format={(v) => `${v.toFixed(1)} kg/h`}
                          gutter={188}
                          rowHeight={32}
                          showTrack
                        />
                      </div>

                      <Commentable name="EmissionsByClassMine">
                        <div
                          className="min-w-0 rounded-xl border p-4"
                          style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                        >
                          <h3
                            className="text-[12.5px] font-semibold"
                            style={{ color: 'var(--ink-primary)' }}
                          >
                            Emissions intensity by machine class and mine
                          </h3>
                          <p
                            className="mb-3 mt-0.5 text-[11px]"
                            style={{ color: 'var(--ink-tertiary)' }}
                          >
                            kg CO₂-e per operating hour, mean of machines
                          </p>
                          <BarChart
                            title="Mean kilograms of CO2 equivalent per operating hour, by machine class and mine"
                            rows={view.classMine}
                            format={(v) => `${v.toFixed(1)} kg/h`}
                            gutter={176}
                            rowHeight={24}
                            showTrack
                          />
                        </div>
                      </Commentable>
                    </div>
                  </>
                ) : (
                  <SkeletonCardGrid count={6} cols={3} />
                )}
              </SceneSection>
            </Commentable>
          </Disclosure>

          <p className="sr-only" role="status">
            {loading && !view ? 'Loading the cost ledger.' : 'Cost summary ready.'}
          </p>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

/**
 * Axis-tick money. `fmtk` renders zero as `A$0k`, which is right on a row
 * total and noise at an axis origin, so the origin gets a bare `0`.
 */
const axisMoney = (value: number): string => (value === 0 ? '0' : fmtk(value));

/** Site display name for a mine code, from the tiles already computed. */
function MINE_NAME(
  mine: MineCode,
  tiles: readonly { mine: MineCode; name: string }[],
): string {
  return tiles.find((t) => t.mine === mine)?.name ?? mine;
}

export default CostSummaryScene;
